import type { PbjCategoryScore } from "../../../utils/models";
import { bandPointsForValue } from "./bandBuffer.utils";
import type { PlanItem, PlanItemCategoryBreakdown } from "./recommendations.constants";

// Points a `rawValue` earns on the category's band ladder, scored against the
// UNBUFFERED (published CMS) bounds — see the parity note below. The band walk
// itself lives in `bandBuffer.utils` so the FE has exactly one mirror of the
// backend `_band_matches`.
export const pointsForHppd = (cat: PbjCategoryScore, rawValue: number): number => bandPointsForValue(cat, rawValue, "cms");

// FE analog of the backend `_SEQUENTIAL_BAND_EPSILON` in
// `backend/services/pbj_model/greedy.py::_to_steps`. A stored gap was computed
// without the greedy's per-iteration float nudges, so `baseline + gap` lands a
// few microsteps SHY of the band cutoff the greedy actually crossed (e.g.
// 0.677995 against a 0.678 cutoff). This epsilon nudges the value strictly
// inside the next half-open `[min, max)` band so re-scoring stays in step with
// the backend. Kept identical to the backend constant (1e-4): large enough to
// cover cumulative float drift, far smaller than the narrowest band (> 0.07).
// Note: parity with the backend is exact only when the band-range buffer is 0 —
// the FE scores against the UNBUFFERED band ladder (raw minValue/maxValue),
// whereas the backend `_score_band`/`_next_band_min_value` apply buffered
// bounds when `band_range_buffer_pct > 0`. This is intentional: the FE matches
// the unbuffered Scoring Threshold table (see RecommendationsSidebar.tsx:76).
const SEQUENTIAL_BAND_EPSILON = 1e-4;

// Marginal points a single per-category `hppdGap` earns over `before`, plus the
// advanced baseline. The nudge is applied BEFORE scoring `after` — mirroring the
// backend `_to_steps` (`running_raw += gap + EPS` first, THEN re-score) — so a
// BE-suggested gap landing a hair below its band cutoff still credits the
// crossing. Scoring `before + gap` raw and only nudging the NEXT item's baseline
// under-credited each such crossing by one band (DAT-497: the Action Plan read
// "Need 5 more points" while the BE plan had met the target). This is the single
// shared scorer for every points figure derived from a stored gap — the
// cumulative pass below and the sidebar's per-rec previews.
export const scoreGapFromBaseline = (cat: PbjCategoryScore, before: number, hppdGap: number): { gain: number; after: number } => {
	const after = before + hppdGap + SEQUENTIAL_BAND_EPSILON;
	return { gain: Math.max(0, pointsForHppd(cat, after) - pointsForHppd(cat, before)), after };
};

// HPPD categories the recommendation levers couple to ("1"=Total, "2"=RN,
// "3"=Weekend). Turnover/Admin categories are never advanced by a plan item.
const HPPD_CATEGORY_IDS = ["1", "2", "3"] as const;
const HPPD_CATEGORY_ID_SET = new Set<string>(HPPD_CATEGORY_IDS);

// The Adjusted Weekend HPPD category id (mirror of the BE `scoring.py` category ids).
// Weekend measures/recs use the weekend-only day denominator; everything else uses the
// full calendar-day denominator. Centralized here so the HPPD/day-denominator math in
// recMath, CategoryBreakdownTable, and ManageManualHoursDialog reads from one constant
// instead of re-hardcoding "3".
export const WEEKEND_CATEGORY_ID = "3";

// Days/week each staffing lift lands on: weekend-only recs add hours on Sat+Sun (2),
// every-day recs add hours across all 7. Shared by ActionItems, CategoryBreakdownTable,
// and planOutcome so the per-week hours conversion is defined once.
export const DAYS_PER_WEEK_WEEKEND = 2;
export const DAYS_PER_WEEK_FULL = 7;

// Per-item result of the cumulative pass.
export type CumulativeItemResult = {
	// Marginal points this item earned over the prefix of items added before it.
	points: number;
	// Per-category marginal points, keyed by category id, so ActionItems' breakdown
	// sub-rows sum to `points`.
	pointsByCategory: Record<string, number>;
};

export type CumulativePassResult = {
	// Per-item marginal points keyed by PlanItem id.
	byItemId: Record<string, CumulativeItemResult>;
	// Sum of every item's marginal points — the plan's true cumulative gain.
	total: number;
};

// Seed a `running` per-category adjusted-HPPD map from each HPPD category's
// `rawValue`. Categories without a band ladder (turnover/admin) are skipped since
// plan items never touch them.
const seedRunning = (categories: PbjCategoryScore[]): Record<string, number> => {
	const running: Record<string, number> = {};
	for (const cat of categories) {
		if (HPPD_CATEGORY_ID_SET.has(cat.id)) {
			running[cat.id] = cat.rawValue;
		}
	}
	return running;
};

// Index HPPD categories by id for O(1) lookup during the walk.
const indexHppdCategories = (categories: PbjCategoryScore[]): Record<string, PbjCategoryScore> => {
	const byId: Record<string, PbjCategoryScore> = {};
	for (const cat of categories) {
		if (HPPD_CATEGORY_ID_SET.has(cat.id)) byId[cat.id] = cat;
	}
	return byId;
};

// Advance `running` by a single plan item's per-category HPPD gaps, mutating the
// map in place. Returns the marginal points that item earns over the current
// `running` baseline (the prefix of items already applied). Mirrors the backend
// greedy's `_to_steps` sequential attribution: each touched category is scored
// via `scoreGapFromBaseline` (nudge first, then re-score — see that helper), and
// `running` advances to the same nudged value the points were scored at.
const applyItem = (item: PlanItem, byId: Record<string, PbjCategoryScore>, running: Record<string, number>): CumulativeItemResult => {
	const pointsByCategory: Record<string, number> = {};
	let points = 0;
	// An item without a stored breakdown can't be scored cumulatively (shouldn't
	// happen once every add/drag attaches one) — it contributes 0 and leaves running.
	const breakdown: PlanItemCategoryBreakdown[] = item.breakdown ?? [];
	for (const bucket of breakdown) {
		const cat = byId[bucket.categoryId];
		if (!cat) continue;
		const { gain, after } = scoreGapFromBaseline(cat, running[bucket.categoryId], bucket.hppdGap);
		pointsByCategory[bucket.categoryId] = gain;
		points += gain;
		running[bucket.categoryId] = after;
	}
	return { points, pointsByCategory };
};

// Cumulative-scoring pass — the FE analog of `backend/services/pbj_model/greedy.py
// ::_to_steps`. Walks `selectedPlan` in add-order, crediting each item the marginal
// point gain over the items added BEFORE it. Because band scoring is a nonlinear
// step function, this is the only attribution that (a) never under-credits (a later
// item scores from the HPPD already added by earlier items) and (b) never
// double-credits (two items crossing the same band edge split the points by add
// order). The total is invariant to how the lift is split across items.
export const computeCumulativePass = (selectedPlan: PlanItem[], categories: PbjCategoryScore[]): CumulativePassResult => {
	const byId = indexHppdCategories(categories);
	const running = seedRunning(categories);
	const byItemId: Record<string, CumulativeItemResult> = {};
	let total = 0;
	for (const item of selectedPlan) {
		const result = applyItem(item, byId, running);
		byItemId[item.id] = result;
		total += result.points;
	}
	return { byItemId, total };
};

// Effective per-category baseline `running` map after applying a PREFIX of
// `selectedPlan` — every item strictly before `stopId`, or the entire plan when
// `stopId` is undefined (a not-yet-selected rec is scored against everything
// currently in the plan). The sidebar uses this so a rec's "+X pts" preview scores
// against `rawValue + cumulative shift of the items added before it`, making the
// preview equal that rec's Action Plan row.
export const effectiveBaselineForPrefix = (selectedPlan: PlanItem[], categories: PbjCategoryScore[], stopId?: PlanItem["id"]): Record<string, number> => {
	const byId = indexHppdCategories(categories);
	const running = seedRunning(categories);
	for (const item of selectedPlan) {
		if (stopId !== undefined && item.id === stopId) break;
		applyItem(item, byId, running);
	}
	return running;
};
